import { CardRadioForm } from '@/global/components/Form'
import SelectCompetencia, {
    useCompetencia,
} from '@/global/components/Selects/SelectCompetencia'
import { SelectFuncionarios } from '@/global/components/Selects/SelectFuncionarios'
import Grid from '@/global/components/ui/Grid'
import { Label } from '@/global/components/ui/label'
import Dialog from '@/global/guides/Dialog'
import { useTransmit } from '@/hooks/useTransmit'
import { useCalculoOutletContext } from '@/pages/CalculoPonto.context'
import dayjs from 'dayjs'
import { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { espelhoPonto } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { z } from 'zod'
import { EventEspelhoPdfMessage } from './types'

/** Máximo de competências por download — precisa casar com o backend. */
const MAX_COMPETENCIAS = 12

const schema = z.object({
    formato: z.enum(['pdf', 'zip']),
})

type FormInput = z.infer<typeof schema>

type Payload = {
    competencias: string[]
    funcionarioIds?: number[]
    formato: FormInput['formato']
}

/** Lista as competências (YYYY-MM) de inicial até final, inclusive. */
function listPeriodo(inicial: string, final: string): string[] {
    const inicio = dayjs(inicial, 'YYYY-MM')
    const fim = dayjs(final, 'YYYY-MM')
    const lista: string[] = []
    let atual = inicio
    while (!atual.isAfter(fim, 'month')) {
        lista.push(atual.format('YYYY-MM'))
        atual = atual.add(1, 'month')
    }
    return lista
}

export default function ExportarPeriodoModal() {
    const empresaId = useStore(state => state.companyId)
    /** Funcionários marcados na tabela entram pré-selecionados. */
    const { funcionarioIds: selecionadosNaTabela } = useCalculoOutletContext()

    const [inicial, setInicial] = useCompetencia()
    const [final, setFinal] = useCompetencia()
    const [loading, setLoading] = useState(false)
    const [funcionarioIds, setFuncionarioIds] =
        useState<number[]>(selecionadosNaTabela)

    const competencias = listPeriodo(inicial, final)
    const excedeLimite = competencias.length > MAX_COMPETENCIAS
    /** Seleção vazia = todos, expandidos pelo backend (inclui rescindidos). */
    const todos = funcionarioIds.length === 0

    const subscribe = useTransmit<EventEspelhoPdfMessage>({
        onMessage: async ({ status, data }, unsubscribe) => {
            switch (status) {
                case 'ERRO':
                    toast.error(data.message)
                    await unsubscribe()
                    break
                case 'FINALIZADO':
                    if (data.pdf) {
                        await espelhoPonto.calculos.file(`zip/${data.pdf}`)
                    }
                    if (data.zip) {
                        await espelhoPonto.calculos.file(`zip/${data.zip}`)
                    }
                    await unsubscribe()
                    toast.success('Espelhos gerados com sucesso')
                    break
            }
        },
    })

    const handleSubmit: SubmitHandler<FormInput> = async ({ formato }) => {
        if (competencias.length === 0) {
            return toast.warning(
                'A competência inicial deve ser anterior ou igual à final',
            )
        }

        if (excedeLimite) {
            return toast.warning(
                `Selecione no máximo ${MAX_COMPETENCIAS} competências — divida o período em partes`,
            )
        }

        try {
            const canal = await espelhoPonto.post<Payload, string>(
                `periodo?empresaId=${empresaId}`,
                {
                    competencias,
                    formato,
                    ...(todos ? {} : { funcionarioIds }),
                },
            )

            toast.info(
                'Estamos gerando os espelhos. Você será notificado quando estiverem prontos.',
            )

            return subscribe(canal)
        } catch (error) {
            throw toast.error(
                error instanceof Error
                    ? error.message
                    : 'Erro ao gerar o arquivo',
            )
        }
    }

    const resumoCompetencias = (() => {
        if (competencias.length === 0) return 'Selecione um período válido.'
        if (excedeLimite) {
            return `Máximo de ${MAX_COMPETENCIAS} competências — o período tem ${competencias.length}.`
        }
        return `${competencias.length} competência(s) serão geradas.`
    })()

    return (
        <Dialog.Root
            width="3xl"
            loading={loading}
            onSubmit={v => {
                setLoading(true)
                return (handleSubmit(v) as any).finally(() => setLoading(false))
            }}
            schema={schema}
            defaultValues={{ formato: 'pdf' }}
        >
            <Dialog.Title>Exportar espelhos por período</Dialog.Title>
            <Dialog.Content>
                <Grid container>
                    <Grid item xs={12}>
                        <Label>Período</Label>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SelectCompetencia
                            label="Competência inicial"
                            value={inicial}
                            onChange={setInicial}
                        />{' '}
                        <span className="text-xs text-secondary-foreground ml-3 mt-1">
                            {resumoCompetencias}
                        </span>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SelectCompetencia
                            label="Competência final"
                            value={final}
                            onChange={setFinal}
                        />
                    </Grid>

                    <Grid item xs={12} className="h-4" children={<></>} />

                    <Grid item xs={12}>
                        <Label>Funcionários</Label>
                    </Grid>
                    <Grid item xs={12}>
                        <SelectFuncionarios
                            variant="form"
                            incluirRescindidos
                            value={funcionarioIds}
                            onChange={setFuncionarioIds}
                            helperText={
                                todos
                                    ? 'Nenhum selecionado — serão exportados todos os funcionários com cálculo no período.'
                                    : `${funcionarioIds.length} funcionário(s) selecionado(s).`
                            }
                        />
                    </Grid>

                    <Grid item xs={12} className="h-4" children={<></>} />

                    <Grid item xs={12}>
                        <CardRadioForm
                            name="formato"
                            label="Formato do download"
                            options={[
                                {
                                    value: 'pdf',
                                    titulo: 'PDF único',
                                    descricao:
                                        'Junta todos os espelhos em um único arquivo PDF.',
                                },
                                {
                                    value: 'zip',
                                    titulo: 'ZIP',
                                    descricao:
                                        'Um arquivo PDF por espelho, compactados em um ZIP.',
                                },
                            ]}
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton
                    closeOnSubmit
                    disabled={!competencias.length || excedeLimite}
                >
                    Exportar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}
